import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, X, Users, Check, Clock } from "lucide-react";

export const FriendsCard = () => {
  const [friends, setFriends] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [f, r] = await Promise.all([api.get("/friends"), api.get("/friends/requests")]).catch(() => [null, null]);
    if (f) setFriends(f.data.friends);
    if (r) { setIncoming(r.data.incoming); setOutgoing(r.data.outgoing); }
  };
  useEffect(() => { load(); }, []);

  const sendRequest = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/friends/request", { email: email.trim() });
      toast.success(`Friend request sent to ${data.to.name}`);
      setEmail("");
      load();
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  const respond = async (id, action) => {
    await api.post(`/friends/request/${id}/${action}`).catch(() => {});
    toast.success(action === "accept" ? "Friend added!" : "Request declined");
    load();
  };

  const remove = async (id) => { await api.delete(`/friends/${id}`).catch(() => {}); load(); };

  return (
    <div className="bg-[#11111A] border border-cyan-500/20 p-8" data-testid="friends-card">
      <div className="flex items-center gap-3 mb-6">
        <Users className="w-6 h-6 text-[#00F0FF]" />
        <h2 className="font-head font-700 text-xl text-slate-50">FRIENDS</h2>
      </div>

      <form onSubmit={sendRequest} className="flex gap-2 mb-5">
        <input
          data-testid="friend-email-input" type="email" value={email}
          onChange={(e) => setEmail(e.target.value)} placeholder="friend@email.com"
          className="flex-1 bg-[#0A0A14] border border-slate-700 px-3 py-2 text-sm text-slate-100 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition-colors duration-200"
        />
        <button data-testid="add-friend-btn" type="submit" disabled={loading}
          className="font-arcade text-base px-4 py-2 bg-[#00F0FF] text-black border border-[#00F0FF] hover:shadow-[3px_3px_0px_#FF006E] transition-transform duration-200 flex items-center gap-1.5 disabled:opacity-60">
          <UserPlus className="w-4 h-4" /> REQUEST
        </button>
      </form>

      {incoming.length > 0 && (
        <div className="mb-5" data-testid="incoming-requests">
          <p className="font-arcade text-base text-[#F5C842] mb-2">REQUESTS</p>
          <ul className="space-y-2">
            {incoming.map((r) => (
              <li key={r.id} className="flex items-center justify-between px-3 py-2 border border-[#F5C842]/30">
                <span className="text-sm text-slate-200 truncate">{r.user?.name} <span className="text-slate-600">· {r.user?.email}</span></span>
                <div className="flex gap-2 shrink-0">
                  <button data-testid={`accept-request-${r.id}`} onClick={() => respond(r.id, "accept")} className="p-1.5 border border-[#00F0FF]/50 text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black transition-colors duration-200"><Check className="w-4 h-4" /></button>
                  <button data-testid={`decline-request-${r.id}`} onClick={() => respond(r.id, "decline")} className="p-1.5 border border-[#FF006E]/50 text-[#FF006E] hover:bg-[#FF006E] hover:text-black transition-colors duration-200"><X className="w-4 h-4" /></button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {friends.length === 0 && outgoing.length === 0 ? (
        <p className="font-arcade text-base text-slate-600">NO FRIENDS YET — SEND A REQUEST TO COMPETE</p>
      ) : (
        <ul className="space-y-2" data-testid="friends-list">
          {friends.map((f) => (
            <li key={f.id} className="flex items-center justify-between px-3 py-2 border border-slate-800">
              <span className="text-sm text-slate-200 truncate">{f.name} <span className="text-slate-600">· {f.email}</span></span>
              <button data-testid={`remove-friend-${f.id}`} onClick={() => remove(f.id)} className="p-1 text-slate-500 hover:text-[#FF006E] transition-colors duration-200"><X className="w-4 h-4" /></button>
            </li>
          ))}
          {outgoing.map((o) => (
            <li key={o.id} className="flex items-center justify-between px-3 py-2 border border-slate-800/60">
              <span className="text-sm text-slate-400 truncate">{o.user?.name} <span className="text-slate-600">· {o.user?.email}</span></span>
              <span className="inline-flex items-center gap-1 font-arcade text-sm text-slate-500"><Clock className="w-3.5 h-3.5" /> PENDING</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
